import { supabase } from '../supabase';
import { UniqueValueAnalysis } from '../utils/types';

export async function fetchUniqueValues(
  table: string,
  column: string,
  filters?: {
    exporterId?: number;
    speciesId?: number;
    season?: string;
  }
): Promise<UniqueValueAnalysis[]> {
  try {
    // Use RPC function for efficient aggregation in the database
    // This avoids loading all records into the browser
    const { data, error } = await supabase.rpc('get_unique_values_with_count', {
      p_table: table,
      p_column: column,
      p_exporter_id: filters?.exporterId || null,
      p_species_id: filters?.speciesId || null,
      p_season: filters?.season || null,
    });

    if (error) {
      console.error('Error calling get_unique_values_with_count:', error);
      throw error;
    }

    if (!data || data.length === 0) return [];

    // Process the aggregated data from database
    const valueMap = new Map<
      string,
      { count: number; lastSeen: string; examples: string[] }
    >();

    data.forEach((item: any) => {
      let value = '';

      // Handle known special cases
      if (column === 'format') {
        value = standardizeFormat(item.value?.toString() || '');
      } else {
        value = item.value?.toString() || '';
      }

      valueMap.set(value, {
        count: item.count,
        lastSeen: item.last_seen,
        examples: item.example_ids ? item.example_ids.slice(0, 3) : [],
      });
    });

    // Convert to array and find similar values
    const analysisResults: UniqueValueAnalysis[] = Array.from(
      valueMap.entries()
    ).map(([value, stats]) => {
      const similarValues = Array.from(valueMap.keys()).filter(
        (otherValue) =>
          otherValue !== value &&
          calculateLevenshteinDistance(
            value.toLowerCase(),
            otherValue.toLowerCase()
          ) <= 2
      );

      return {
        value,
        count: stats.count,
        lastSeen: stats.lastSeen,
        examples: stats.examples,
        similarValues: similarValues.length > 0 ? similarValues : undefined,
      };
    });

    return analysisResults
      .filter((item) => item.value !== '') // Exclude empty values
      .sort((a, b) => (b.count || 0) - (a.count || 0));
  } catch (error) {
    console.error('Error in fetchUniqueValues:', error);
    throw error;
  }
}

function standardizeFormat(format: string): string {
  const upperFormat = format.toUpperCase().trim();
  return upperFormat;
}

function calculateLevenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + substitutionCost
      );
    }
  }

  return matrix[b.length][a.length];
}
